import React, { useState, useEffect, memo, useCallback, useMemo } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import Button from 'antd/es/button';
import message from 'antd/es/message';
import Radio from 'antd/es/radio';
import Checkbox from 'antd/es/checkbox';
import EyeInvisibleOutlined from '@ant-design/icons/es/icons/EyeInvisibleOutlined';
import EyeOutlined from '@ant-design/icons/es/icons/EyeOutlined';

import AuthRedirect from './AuthRedirect';
import MainLoginSignupLayout from './LoginLayouts';
import styles from '../Styles/publicRoutes/style.module.less';
import signupStyles from '../Styles/publicRoutes/signup.module.less';
import { LOADING } from '../Constants/Messages';
import { getFirstLastName, getParameterFromUrl, passwordValidation } from '../Utils/HelperFunctions';
import { isBeamUser } from '../Utils/usersTypes';
import { openAPI } from '../Utils/ApiCalls';
import { ATTENTIVE_AI, BEAM_AI, PRIVACY_POLICY, REGISTER, TERMS_OF_USE, VALIDATE_REGISTER } from '../Constants/Urls';
import { ClientLogos } from './ClientsLogos';
import { USLawnsRestrictModal } from '../Components/USLawnsModal';
import { NonUSARestrictModal } from '../Components/NonUSARestrictModal';
import { KNOWN_EXCEPTIONS, USAGE_MODE } from '../Constants/Constant';
import { classNames } from '../Utils/pureHelpers';
import { useCompanyConfig } from '../Stores/CompanyConfig';
import { useUserDetails } from '../Stores/UserDetails';
import { useDevice } from '../Stores/Device';
import { useBreakpoint } from '../Utils/Responsive';
import {
  answerSchema,
  emailSchema,
  orgSchema,
  passwordSchema,
  phoneSchema,
  userNameSchema
} from '../validations/SignupValidations';

const Signup = (props: any) => {
  const { loginpageContent } = styles;
  const breakpointClassName = useBreakpoint();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialName = params.get('name') || '';
  const initialEmail = params.get('email') || '';
  const initialPhone = params.get('phone') || '';

  const [org, setOrg] = useState('');
  const [email, setEmail] = useState<string>(initialEmail);
  const [name, setName] = useState<string>(initialName);
  const [phone, setPhone] = useState<string>(initialPhone);
  const [invited, setInvited] = useState(false);
  const [uslawnsModal, setUsLawnsModal] = useState(false);
  const [nonUSAModal, setNonUSAModal] = useState(false);

  const [dispatchUserDetails] = useUserDetails(useShallow(state => [state.dispatch]));
  // @ts-expect-error
  const [companyLogo] = useCompanyConfig(useShallow(state => [state.comp_config?.logo]));
  const [mobileView] = useDevice(useShallow(state => [state?.screen?.mobileView]));

  const fromInvite = getParameterFromUrl('invite');
  const beamUser: boolean = isBeamUser();
  const { state, pathname } = useLocation();
  const navigate = useNavigate();
  const isSignupPage: Boolean = pathname.includes('/signup');

  useEffect(() => {
    setEmail(initialEmail);
    setName(initialName);
    setPhone(initialPhone);

    const action = getParameterFromUrl('action');

    if (fromInvite) {
      // @ts-expect-error
      setOrg(getParameterFromUrl('company'));
      setInvited(true);
    }
    if (action === 'verify') {
      const token = getParameterFromUrl('token');
      const userID = getParameterFromUrl('user_id');
      const data = {
        user_id: userID,
        token
      };
      const loadingMsg = message.loading(LOADING);
      openAPI(VALIDATE_REGISTER, data)
        .then(_user => {
          loadingMsg();
          dispatchUserDetails({ type: 'SET_LOGIN', payload: { isLoggedIn: true, user: _user } });
          // @ts-expect-error
          const isBlueprintMode = _user?.construction_takeoff;
          navigate(isBlueprintMode ? '/blueprint' : '/');
        })
        .catch(() => {
          loadingMsg();
        });
    }
  }, [initialEmail, initialName, initialPhone]);

  // @ts-expect-error
  const handleKnownSignupExceptions = errorType => {
    let msg;
    switch (errorType) {
      case KNOWN_EXCEPTIONS.SOCIAL_SIGNUP_EXCEPTION:
        msg = 'Access denied due to issue with social account. Please try signing up with email instead';
        message.error(msg);
        break;
      case KNOWN_EXCEPTIONS.US_SIGNUP_RESTRICTION:
        setNonUSAModal(true);
        break;
      case KNOWN_EXCEPTIONS.US_LAWNS_EXCEPTION:
        setUsLawnsModal(true);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (state?.exception) {
      handleKnownSignupExceptions(state.exception);
      navigate('.', { replace: true, state: {} });
    }
  }, []);

  const onSuccess = useCallback(
    user => {
      if (fromInvite) {
        dispatchUserDetails({ type: 'SET_LOGIN', payload: { isLoggedIn: true, user } });
        const isBlueprintMode = user?.construction_takeoff;
        navigate(isBlueprintMode ? '/blueprint' : '/');
      } else {
        navigate('/thankyou', { state: { showThankYou: true } });
      }
    },
    [fromInvite]
  );

  const onError = useCallback(err => {
    handleKnownSignupExceptions(err?.response?.data?.error_type);
  }, []);

  return (
    <MainLoginSignupLayout className={breakpointClassName} logo={companyLogo} isSignupPage={isSignupPage}>
      <div className={loginpageContent}>
        <SignupHeading beamUser={beamUser} />

        <SignupForm
          org={org}
          setOrg={setOrg}
          email={email}
          setEmail={setEmail}
          invited={invited}
          fromInvite={fromInvite}
          onSuccess={onSuccess}
          onError={onError}
          beamUser={beamUser}
          mobileView={mobileView}
          name={name}
          phone={phone}
          setName={setName}
          setPhone={setPhone}
        />
      </div>

      <ClientLogos />

      <USLawnsRestrictModal modalVisible={uslawnsModal} setModalVisible={setUsLawnsModal} mobileView={mobileView} />

      <NonUSARestrictModal modalVisible={nonUSAModal} setModalVisible={setNonUSAModal} mobileView={mobileView} />
    </MainLoginSignupLayout>
  );
};

export default AuthRedirect(Signup);

const SignupHeading = memo(({ beamUser }: { beamUser: boolean }) => {
  const { mobileAaiLogo, beamAiLogo } = styles;

  return (
    <>
      <a href={beamUser ? BEAM_AI : ATTENTIVE_AI} target='_blank'>
        <img
          src={
            beamUser
              ? 'https://20155995.fs1.hubspotusercontent-na1.net/hubfs/20155995/1700543164573.jpg'
              : 'https://storage.googleapis.com/falcon-shared-images-front-end/assets/svgs/logo-only-dark.svg'
          }
          alt='Attentive Logo'
          className={beamUser ? beamAiLogo : mobileAaiLogo}
        />
      </a>

      <h1 className='fw-bold-3 mb-3'>Get started with {beamUser ? 'Beam AI' : 'Automeasure'}</h1>
      <p className='login-meta'>
        Already have an account?{' '}
        <NavLink className='underline' to='/login'>
          Log in here
        </NavLink>
      </p>
    </>
  );
});

const SignupTerms = memo(() => {
  return (
    <p className='login-meta mt-3'>
      By signing up, you agree to our{' '}
      <a href={TERMS_OF_USE} target='_blank'>
        Terms of Use
      </a>{' '}
      and{' '}
      <a href={PRIVACY_POLICY} target='_blank'>
        Privacy Policy
      </a>
    </p>
  );
});

const SignupInput = ({ type, id, value, disabled, onChange, label, small }: $TSFixMe) => {
  const { smallInput } = signupStyles;

  return (
    <div className={classNames('form-group', small ? smallInput : '')}>
      <input
        type={type}
        id={id}
        placeholder=''
        value={value}
        disabled={disabled || null}
        onChange={onChange}
        maxLength={200}
        required
      />
      <label htmlFor={id}>{label}</label>
    </div>
  );
};

const PasswordInput = memo(({ password, setPassword }: $TSFixMe) => {
  const [showPsw, setShowPsw] = useState(false);
  const { smallInput } = signupStyles;

  return (
    <div className={classNames('form-group', smallInput)}>
      <input
        type={showPsw ? 'text' : 'password'}
        id='signup-password'
        placeholder=' '
        value={password}
        onChange={e => setPassword(e.target.value)}
        required
      />
      <label htmlFor='signup-password'>Password</label>
      <span onClick={() => setShowPsw(!showPsw)} className='eye-icon'>
        {showPsw ? <EyeOutlined /> : <EyeInvisibleOutlined />}
      </span>
    </div>
  );
});

const ModeSelection = memo(({ defaultMode, setMode }: $TSFixMe) => {
  const { radioLabel } = signupStyles;
  const options = useMemo(
    () => [
      {
        value: USAGE_MODE.AERIAL,
        label: 'Aerial takeoffs'
      },
      {
        value: USAGE_MODE.BLUEPRINT,
        label: 'Blueprint takeoffs'
      },
      {
        value: USAGE_MODE.BOTH,
        label: 'Both'
      }
    ],
    []
  );

  return (
    <div className='form-group'>
      <div className={radioLabel}>Which kind of takeoffs are you looking to automate with Attentive?</div>
      <Radio.Group options={options} defaultValue={defaultMode} onChange={e => setMode(e.target.value)} />
    </div>
  );
});

interface UserData {
  email: string;
  password: string;
  is_invited: boolean;
  company: string;
  phone_number: string;
  first_name: string | undefined;
  is_beam_signup?: boolean;
  is_construction_signup?: boolean;
  last_name?: string;
  heard_about_attentive?: string;
  token?: string | null;
  website_url?: string;
}

const SignupForm = memo(
  ({
    org,
    setOrg,
    email,
    setEmail,
    invited,
    fromInvite,
    onSuccess,
    onError,
    beamUser,
    name,
    phone,
    setName,
    setPhone
  }: $TSFixMe) => {
    const { signupForm, signupRow, wrap } = signupStyles;
    const { loginpageBtn } = styles;

    const [isLoading, setIsLoading] = useState(false);
    const [password, setPassword] = useState('');
    const [answer, setAnswer] = useState('');
    const [websiteUrl, setWebsiteUrl] = useState('');
    const [mode, setMode] = useState(USAGE_MODE.AERIAL);

    const handleSignup = useCallback(
      async e => {
        e.preventDefault();

        const passwordError = passwordValidation(password);

        if (passwordError) {
          message.error(passwordError);
          return null;
        }

        const [firstName, lastName] = getFirstLastName(name);

        try {
          await userNameSchema.validate(name);
          await orgSchema.validate(org);
          await phoneSchema.validate(phone);
          await emailSchema.validate(email);
          await passwordSchema.validate(password);
          if (answer) await answerSchema.validate(answer);
        } catch (validationError: any) {
          message.error(validationError.message);
          return null;
        }

        const userData: UserData = {
          email,
          password,
          is_invited: invited,
          company: org,
          phone_number: phone,
          first_name: firstName
        };

        if (beamUser) userData.is_beam_signup = true;
        else {
          userData.is_construction_signup = mode === USAGE_MODE.BLUEPRINT;
          userData.website_url = websiteUrl;
        }

        if (lastName) userData.last_name = lastName;
        if (answer) userData.heard_about_attentive = answer;
        if (fromInvite) userData.token = getParameterFromUrl('token');

        setIsLoading(true);
        openAPI(REGISTER, userData)
          .then(_user => {
            setEmail('');
            setPassword('');
            setName('');
            setOrg('');
            setPhone('');
            setAnswer('');
            setWebsiteUrl('');
            onSuccess(_user);
          })
          .catch(err => onError(err))
          .finally(() => setIsLoading(false));

        if (process.env.APP_ENV === 'prod') {
          const callback = function callback() {
            // @ts-expect-error
            if (typeof url !== 'undefined') {
              // @ts-expect-error
              window.location = url;
            }
          };
          // @ts-expect-error
          gtag('event', 'conversion', {
            send_to: 'AW-475497667/zxkFCPKa-vABEMOJ3uIB',
            event_callback: callback
          });
        }
        return null;
      },
      [name, email, password, invited, org, phone, answer, fromInvite, mode, websiteUrl]
    );

    return (
      <form className={signupForm} onSubmit={handleSignup}>
        <div className={signupRow}>
          <SignupInput
            type='text'
            id='signup-name'
            value={name}
            // @ts-expect-error
            onChange={e => setName(e.target.value)}
            label='Name'
            small
          />
          <SignupInput
            type='text'
            id='signup-org'
            value={org}
            disabled={invited}
            // @ts-expect-error
            onChange={e => setOrg(e.target.value)}
            label='Company Name'
            small
          />
        </div>

        <div className={signupRow}>
          <SignupInput
            type='number'
            id='signup-phone'
            value={phone}
            // @ts-expect-error
            onChange={e => setPhone(e.target.value)}
            label='Phone No.'
            small
          />
          <SignupInput
            type='email'
            id='signup-email'
            value={email}
            disabled={invited}
            // @ts-expect-error
            onChange={e => setEmail(e.target.value)}
            label='Email ID'
            small
          />
        </div>

        <div className={`${signupRow} ${wrap}`}>
          <PasswordInput password={password} setPassword={setPassword} />

          {!invited ? (
            <SignupInput
              type='text'
              id='signup-question'
              value={answer}
              // @ts-expect-error
              onChange={e => setAnswer(e.target.value)}
              label='How did you hear about us?'
              small
            />
          ) : null}
        </div>

        {!beamUser && (
          <>
            <SignupInput
              type='text'
              id='website-url'
              value={websiteUrl}
              // @ts-expect-error
              onChange={e => setWebsiteUrl(e.target.value)}
              label='Website Url'
              small
            />
            <ModeSelection defaultMode={mode} setMode={setMode} />
          </>
        )}

        <Checkbox
          className='mt-3 font-13'
          // @ts-expect-error
          required>
          Yes, I would like to receive marketing communications through SMS, calls or any other relevant medium. You can
          unsubscribe at any moment. For more information read our&nbsp;
          <a target='_blank' href='https://attentiveinc-cs.notion.site/Privacy-Policy-0c00e8f4fe8d4ab88d666c906f402045'>
            Privacy Policy.
          </a>
        </Checkbox>

        <Button className={`${loginpageBtn} mt-3`} htmlType='submit' loading={isLoading} type='primary'>
          Sign up now!
        </Button>

        <SignupTerms />
      </form>
    );
  }
);
